import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import {
  T, fmt, Card, Boton, estiloInput,
  Titulo, Aviso, Vacio, Cargando,
} from '../lib/ui';
import { mensajeDeError } from './Gastos';

// ════════════════════════════════════════════════════════════════════
// Verificación de proveedores
//
// Antes había que abrir direcciones a mano en el navegador para saber
// si la conexión funcionaba, y eso ni siquiera anda: esas rutas piden
// sesión y el navegador no envía el token. Esto lo muestra desde
// dentro de la aplicación, que es donde corresponde.
// ════════════════════════════════════════════════════════════════════

export function Verificacion({ usuario }) {
  const [estado, setEstado] = useState(null);
  const [fallo, setFallo] = useState('');

  const cargar = () => {
    setFallo('');
    api.estadoSunat()
      .then(setEstado)
      .catch((err) => { setFallo(mensajeDeError(err)); setEstado({}); });
  };

  useEffect(() => { cargar(); }, []);

  if (!estado) return <Cargando />;

  return (
    <>
      <Titulo sub="Estado de las consultas a SUNAT y de los proveedores">
        Verificación
      </Titulo>

      {fallo && <Aviso tipo="bad">{fallo}</Aviso>}

      <Servicios estado={estado} onRecargar={cargar} />
      <FirmasDelEquipo />
      {usuario.rol === 'director' && <UltimosErrores />}
      <ProbarRuc />
      <Problemas />
    </>
  );
}

// ── Estado de los dos servicios ─────────────────────────────────────
function Servicios({ estado, onRecargar }) {
  const ap = estado.apisperu || {};
  const of = estado.sunatOficial || {};
  const consumo = ap.consumo;

  return (
    <>
      {/* APIsPERU */}
      <Card style={{ marginBottom: 12 }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-start', gap: 12,
        }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: T.navy }}>
              APIsPERU
            </div>
            <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>
              Estado y condición del RUC del proveedor
            </div>
          </div>
          <Semaforo ok={ap.configurado} />
        </div>

        {ap.configurado && consumo ? (
          <div style={{ marginTop: 14 }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              fontSize: 13, marginBottom: 5,
            }}>
              <span>{consumo.usadas} consultas este mes</span>
              <span style={{ color: T.muted }}>de {consumo.limite}</span>
            </div>
            <div style={{ height: 8, background: T.warm, borderRadius: 4, overflow: 'hidden' }}>
              <div style={{
                width: `${Math.min(consumo.porcentaje, 100)}%`, height: '100%',
                background: consumo.alerta ? T.warn : T.ok, borderRadius: 4,
              }} />
            </div>
            <div style={{
              fontSize: 12, marginTop: 6,
              color: consumo.alerta ? T.warn : T.muted,
            }}>
              {consumo.alerta
                ? `Quedan ${consumo.disponibles}. Al agotarse se sigue mostrando lo último guardado.`
                : `Quedan ${consumo.disponibles} disponibles.`}
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 12, color: T.muted, marginTop: 12 }}>
            Falta la variable APISPERU_TOKEN en el panel de Hostinger.
            Después de agregarla hay que reiniciar la aplicación.
          </div>
        )}
      </Card>

      {/* SUNAT oficial */}
      <Card style={{ marginBottom: 12 }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-start', gap: 12,
        }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: T.navy }}>
              API oficial de SUNAT
            </div>
            <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>
              Valida el comprobante: si existe y si fue anulado
            </div>
          </div>
          <Semaforo ok={of.configurado && of.ok} />
        </div>

        <div style={{ fontSize: 12, color: T.muted, marginTop: 12 }}>
          {of.configurado
            ? (of.ok ? 'Las credenciales funcionan.' : of.detalle)
            : 'Faltan las credenciales del Menú SOL. Es gratuita: se sacan en '
              + 'Empresas / Comprobantes de pago / Consulta de Validez / Credenciales de API SUNAT.'}
        </div>
      </Card>

      <Boton variante="suave" onClick={onRecargar}
             style={{ width: '100%', marginBottom: 18 }}>
        Volver a comprobar
      </Boton>
    </>
  );
}

function Semaforo({ ok }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: '.04em',
      padding: '3px 10px', borderRadius: 11, whiteSpace: 'nowrap',
      background: ok ? T.okBg : T.warm,
      color: ok ? T.ok : T.muted,
    }}>
      {ok ? 'CONECTADO' : 'SIN CONFIGURAR'}
    </span>
  );
}

// ── Probar un RUC ───────────────────────────────────────────────────
function ProbarRuc() {
  const [ruc, setRuc] = useState('');
  const [r, setR] = useState(null);
  const [ocupado, setOcupado] = useState(false);

  const consultar = async () => {
    if (ruc.replace(/\D/g, '').length !== 11) return;
    setOcupado(true);
    setR(null);
    try {
      setR(await api.consultarRuc(ruc.replace(/\D/g, '')));
    } catch (err) {
      setR({ error: mensajeDeError(err) });
    }
    setOcupado(false);
  };

  const COLOR = { ok: T.ok, aviso: T.warn, malo: T.bad, sin_verificar: T.muted };

  return (
    <Card style={{ marginBottom: 18 }}>
      <div style={{
        fontSize: 11, color: T.muted, fontWeight: 600,
        letterSpacing: '.05em', marginBottom: 12,
      }}>
        PROBAR UN RUC
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <input
          value={ruc} inputMode="numeric" maxLength={11}
          onChange={(e) => setRuc(e.target.value.replace(/\D/g, ''))}
          onKeyDown={(e) => e.key === 'Enter' && consultar()}
          placeholder="20131312955"
          style={{ ...estiloInput, flex: 1, fontFamily: 'monospace' }}
        />
        <Boton variante="acento" onClick={consultar}
               disabled={ocupado || ruc.length !== 11}>
          {ocupado ? '…' : 'Consultar'}
        </Boton>
      </div>

      {r && (
        <div style={{ marginTop: 14 }}>
          {r.error && <Aviso tipo="bad">{r.error}</Aviso>}

          {r.validacion && !r.validacion.valido && (
            <Aviso tipo="bad">{r.validacion.detalle}</Aviso>
          )}

          {r.evaluacion && (
            <>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 9, marginBottom: 8,
              }}>
                <span style={{
                  width: 10, height: 10, borderRadius: 5, flexShrink: 0,
                  background: COLOR[r.evaluacion.nivel] || T.muted,
                }} />
                <span style={{
                  fontSize: 15, fontWeight: 600,
                  color: COLOR[r.evaluacion.nivel] || T.navy,
                }}>
                  {r.evaluacion.texto}
                </span>
              </div>

              {r.proveedor?.razonSocial && (
                <div style={{ fontSize: 14, marginBottom: 4 }}>
                  {r.proveedor.razonSocial}
                </div>
              )}
              {r.proveedor?.direccion && (
                <div style={{ fontSize: 12, color: T.muted, marginBottom: 8 }}>
                  {r.proveedor.direccion}
                </div>
              )}
              {r.evaluacion.detalle && (
                <div style={{ fontSize: 12, color: T.muted, marginBottom: 8 }}>
                  {r.evaluacion.detalle}
                </div>
              )}

              {r.extra?.facturaElectronica && (
                <div style={{ fontSize: 12, color: T.ok }}>
                  Emite factura electrónica.
                </div>
              )}

              {r.consulta && (
                <div style={{
                  fontSize: 11, color: T.muted, marginTop: 10,
                  paddingTop: 10, borderTop: `1px solid ${T.border}`,
                }}>
                  {r.consulta.origen === 'cache'
                    ? 'Dato guardado: no se gastó consulta de la cuota.'
                    : r.consulta.origen === 'apisperu'
                      ? 'Consultado a APIsPERU ahora mismo.'
                      : r.consulta.detalle}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Card>
  );
}

// ── Proveedores con problema ────────────────────────────────────────
function Problemas() {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.alertasProveedores()
      .then(setD)
      .catch(() => setD({ gastos: [] }));
  }, []);

  if (!d) return <Cargando />;
  const gastos = d.gastos || [];

  return (
    <>
      <div style={{
        fontSize: 11, color: T.muted, fontWeight: 600,
        letterSpacing: '.05em', marginBottom: 10,
      }}>
        GASTOS CON PROVEEDOR OBSERVADO
      </div>

      {gastos.length === 0 ? (
        <Vacio>
          Ningún gasto tiene un proveedor no habido o con RUC de baja.
        </Vacio>
      ) : (
        <>
          <Aviso tipo="alerta">
            {gastos.length} gasto(s) de {d.rucsConProblema} proveedor(es) que
            SUNAT reporta como no habidos o con RUC inactivo. Estos comprobantes
            no dan crédito fiscal.
          </Aviso>

          <div style={{ display: 'grid', gap: 8 }}>
            {gastos.map((g) => (
              <Card key={g.id} style={{ padding: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                      {g.proveedorRazon || g.proveedorRuc}
                    </div>
                    <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
                      {fmt.fecha(g.fecha)} · {g.concepto || 'Sin concepto'}
                      {g.ovId ? ` · ${g.ovId}` : ''}
                    </div>
                  </div>
                  <div style={{
                    fontSize: 13, fontWeight: 600,
                    color: T.navy, whiteSpace: 'nowrap',
                  }}>
                    {fmt.money(g.total, g.moneda)}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}
    </>
  );
}


// ── Últimos fallos del servidor ─────────────────────────────────────
//
// Cuando algo falla, la pantalla muestra una referencia de seis letras.
// Aquí se ve qué hay detrás de esa referencia, sin tener que entrar al
// servidor a buscar el registro.
//
// Se guarda en memoria y se pierde al reiniciar la aplicación: es para
// resolver un problema ahora, no un archivo histórico.
function UltimosErrores() {
  const [d, setD] = useState(null);
  const [abierto, setAbierto] = useState(false);

  const cargar = () => api.erroresRecientes()
    .then((r) => setD(r.errores || []))
    .catch(() => setD([]));

  useEffect(() => { if (abierto) cargar(); }, [abierto]);

  return (
    <Card style={{ marginBottom: 18 }}>
      <div
        onClick={() => setAbierto(!abierto)}
        style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', gap: 12, cursor: 'pointer',
        }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: T.navy }}>
            Últimos fallos
          </div>
          <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>
            Qué hay detrás de una referencia de error
          </div>
        </div>
        <span style={{ fontSize: 13, color: T.brass }}>
          {abierto ? 'Ocultar' : 'Ver'}
        </span>
      </div>

      {abierto && (
        <div style={{ marginTop: 14 }}>
          {!d ? <Cargando /> : d.length === 0 ? (
            <div style={{ fontSize: 13, color: T.muted }}>
              Ningún fallo desde el último reinicio.
            </div>
          ) : (
            <>
              <Boton variante="suave" onClick={cargar}
                     style={{ marginBottom: 12, fontSize: 12, padding: '6px 12px' }}>
                Actualizar
              </Boton>
              <div style={{ display: 'grid', gap: 9 }}>
                {d.map((e) => (
                  <div key={e.referencia} style={{
                    background: T.warm, borderRadius: 8, padding: 11,
                    borderLeft: `3px solid ${T.bad}`,
                  }}>
                    <div style={{
                      display: 'flex', justifyContent: 'space-between',
                      gap: 10, marginBottom: 5,
                    }}>
                      <span style={{
                        fontFamily: 'monospace', fontSize: 12,
                        fontWeight: 700, color: T.bad,
                      }}>
                        {e.referencia}
                      </span>
                      <span style={{ fontSize: 10, color: T.muted }}>
                        {fmt.fechaHora(e.cuando)}
                      </span>
                    </div>
                    <div style={{ fontSize: 11.5, color: T.tinta, marginBottom: 4 }}>
                      {e.ruta}{e.quien ? ` · ${e.quien}` : ''}
                    </div>
                    <div style={{
                      fontSize: 11.5, color: T.bad, fontFamily: 'monospace',
                      wordBreak: 'break-word', marginBottom: 4,
                    }}>
                      {e.codigo ? `[${e.codigo}] ` : ''}{e.mensaje}
                    </div>
                    {e.sql && (
                      <div style={{
                        fontSize: 11, color: T.muted, fontFamily: 'monospace',
                        wordBreak: 'break-word', marginBottom: 4,
                      }}>
                        {e.sql}
                      </div>
                    )}
                    {e.donde?.length > 0 && (
                      <div style={{ fontSize: 10, color: T.muted, fontFamily: 'monospace' }}>
                        {e.donde[0]}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </Card>
  );
}


// ── Quién tiene firma registrada ────────────────────────────────────
//
// La firma en plataforma solo sirve si la gente la registró. Esto
// muestra a quién hay que recordárselo, en vez de descubrirlo cuando
// un documento no se puede contabilizar.
function FirmasDelEquipo() {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.firmasEquipo().then(setD).catch(() => setD({ equipo: [] }));
  }, []);

  if (!d) return null;
  const faltan = (d.equipo || []).filter((p) => !p.tieneFirma);

  return (
    <Card style={{ marginBottom: 18 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', gap: 12, marginBottom: 12,
      }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: T.navy }}>
            Firmas registradas
          </div>
          <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>
            Sin firma no se puede firmar un documento desde la cuenta
          </div>
        </div>
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '.04em',
          padding: '3px 10px', borderRadius: 11, whiteSpace: 'nowrap',
          background: faltan.length === 0 ? T.okBg : T.warnBg,
          color: faltan.length === 0 ? T.ok : T.warn,
        }}>
          {d.conFirma} DE {d.total}
        </span>
      </div>

      {faltan.length === 0 ? (
        <div style={{ fontSize: 13, color: T.ok }}>
          Todo el equipo tiene su firma registrada.
        </div>
      ) : (
        <>
          <div style={{ fontSize: 12.5, color: T.tinta, marginBottom: 9 }}>
            Falta que la registren en <strong>Mi cuenta</strong>:
          </div>
          <div style={{ display: 'grid', gap: 5 }}>
            {faltan.map((p) => (
              <div key={p.id} style={{
                display: 'flex', justifyContent: 'space-between',
                gap: 10, fontSize: 12.5, padding: '5px 0',
                borderBottom: `1px solid ${T.border}`,
              }}>
                <span>{p.nombre}</span>
                <span style={{ color: T.muted, whiteSpace: 'nowrap' }}>
                  {p.centro} · {p.rol.replace(/_/g, ' ')}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}
